import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useContentProtection } from '../../hooks/useContentProtection';
import useSpeechAnalytics from '../../hooks/useSpeechAnalytics';
import SpeechCoaching from '../../components/ai/SpeechCoaching';
import { 
  Play, 
  Pause, 
  Square, 
  ChevronLeft, 
  ChevronRight, 
  Video,
  VideoOff,
  Mic,
  MicOff,
  Clock,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Volume2,
  Send,
  AlertCircle
} from 'lucide-react';

const EvolveLiveSession = () => {
  const { sessionId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Enable content protection (disable right-click, copy, screenshots)
  useContentProtection(true);
  
  // Session data
  const [session, setSession] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  // Recording state
  const [stage, setStage] = useState('setup'); // setup, presenting, qa, analyzing, complete
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  
  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Q&A state - Voice only (no text input)
  const [isAnswering, setIsAnswering] = useState(false);
  const [answerRecordingTime, setAnswerRecordingTime] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [answerEvaluation, setAnswerEvaluation] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [followUpCount, setFollowUpCount] = useState(0);
  const answerTimerRef = useRef(null);
  const answerMediaRecorderRef = useRef(null);
  const answerChunksRef = useRef([]);
  
  // Speech Analytics
  const {
    analytics: speechAnalytics,
    isAnalyzing: isSpeechAnalyzing,
    analyzeTranscript: analyzeSpeech,
    saveAnalyticsToHistory,
    clearAnalytics: clearSpeechAnalytics
  } = useSpeechAnalytics();
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  // Ensure video element has stream when stage changes to presenting or qa
  useEffect(() => {
    if ((stage === 'presenting' || stage === 'qa') && streamRef.current && videoRef.current) {
      // Set srcObject on the floating video preview
      if (videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
      }
    }
  }, [stage]);

  // Memoized stopMediaStream to avoid stale closure
  const stopMediaStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Memoized fetchSessionData
  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [sessionRes, slidesRes] = await Promise.all([
        api.get(`/evolve/sessions/${sessionId}`),
        api.get(`/evolve/sessions/${sessionId}/slides`)
      ]);
      
      setSession(sessionRes.data);
      setSlides(slidesRes.data.slides || []);
      
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/evolve');
    } finally {
      setLoading(false);
    }
  }, [api, sessionId, navigate]);

  useEffect(() => {
    fetchSessionData();
    return () => {
      stopMediaStream();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchSessionData, stopMediaStream]);

  const startMediaStream = async () => {
    try {
      // Build proper constraints - at least audio is always needed for presentation
      const constraints = {
        video: cameraEnabled ? { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        } : false,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (error) {
      console.error('Media access error:', error);
      let errorMessage = 'Camera/Microphone access denied';
      let errorDescription = 'Please allow access to record your presentation';
      
      if (error.name === 'NotAllowedError') {
        errorDescription = 'You denied permission. Please check your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera/microphone found';
        errorDescription = 'Please ensure your device has a camera and microphone.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Device in use';
        errorDescription = 'Your camera or microphone may be used by another application.';
      }
      
      toast.error(errorMessage, { description: errorDescription });
      return null;
    }
  };

  const uploadRecording = useCallback(async (videoBlob) => {
    try {
      const formData = new FormData();
      formData.append('file', videoBlob, `recording_${sessionId}.webm`);
      
      await api.post(`/evolve/sessions/${sessionId}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Presentation recording saved!');
    } catch (error) {
      console.error('Failed to upload recording:', error);
      // Don't show error - video upload is optional
    }
  }, [api, sessionId]);

  const toggleCamera = useCallback(async () => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCameraEnabled(videoTrack.enabled);
      }
    }
  }, []);

  const toggleMic = useCallback(async () => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicEnabled(audioTrack.enabled);
      }
    }
  }, []);

  const startPresenting = async () => {
    // Try to reuse existing stream from setup preview, or create new one
    let stream = streamRef.current;
    
    // Check if existing stream is still active
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();
      const hasActiveVideo = videoTracks.some(t => t.readyState === 'live');
      const hasActiveAudio = audioTracks.some(t => t.readyState === 'live');
      
      if (!hasActiveVideo && !hasActiveAudio) {
        // Stream is dead, need a new one
        stream = null;
      }
    }
    
    // If no valid stream, start a new one
    if (!stream) {
      stream = await startMediaStream();
      if (!stream) return;
    }
    
    // Ensure video element has the stream
    if (videoRef.current && videoRef.current.srcObject !== stream) {
      videoRef.current.srcObject = stream;
    }
    
    setStage('presenting');
    setIsRecording(true);
    setRecordingTime(0);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    
    // Setup media recorder
    try {
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000);
    } catch (error) {
      console.error('MediaRecorder error:', error);
    }
  };

  const finishPresenting = async () => {
    // Stop recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setIsRecording(false);
    
    // Upload recorded video in background
    if (chunksRef.current.length > 0) {
      const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
      uploadRecording(videoBlob);
    }
    
    // Generate Q&A questions
    toast.info('Generating Q&A questions...');
    
    try {
      const qaRes = await api.post(`/evolve/sessions/${sessionId}/qa`);
      setQuestions(qaRes.data.questions || []);
      setStage('qa');
      toast.success('Ready for Q&A!');
    } catch (error) {
      toast.error('Failed to generate questions');
      // Continue anyway with fallback
      setQuestions([
        'What was the main point of your presentation?',
        'How would you respond to a challenging question from the audience?',
        'What key takeaway should the audience remember?'
      ]);
      setStage('qa');
    }
  };

  const startAnswering = async () => {
    // Clear any previous state
    setCurrentTranscript('');
    setAnswerEvaluation(null);
    answerChunksRef.current = [];
    
    // Check microphone permission first
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'denied') {
        toast.error('Microphone access denied', {
          description: 'Please enable microphone access in your browser settings and refresh the page.'
        });
        return;
      }
    } catch (e) {
      // Permissions API not supported, continue anyway
      console.log('Permissions API not supported, proceeding with getUserMedia');
    }
    
    // Start audio recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      });
      
      // Verify we have audio tracks
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        toast.error('No microphone detected', {
          description: 'Please connect a microphone and try again.'
        });
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      
      // Check if track is actually enabled
      const audioTrack = audioTracks[0];
      if (audioTrack.muted) {
        toast.warning('Microphone is muted', {
          description: 'Please unmute your microphone.'
        });
      }
      
      // Now start recording
      setIsAnswering(true);
      setAnswerRecordingTime(0);
      
      // Start timer
      answerTimerRef.current = setInterval(() => {
        setAnswerRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Determine best MIME type
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          answerChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast.error('Recording error occurred', {
          description: 'Please try again.'
        });
        setIsAnswering(false);
        if (answerTimerRef.current) {
          clearInterval(answerTimerRef.current);
        }
      };
      
      answerMediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      
    } catch (error) {
      console.error('Microphone access error:', error);
      
      let errorMessage = 'Could not access microphone.';
      let errorDescription = 'Please check your permissions.';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone permission denied';
        errorDescription = 'Click the microphone icon in your browser address bar to allow access.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found';
        errorDescription = 'Please connect a microphone and refresh the page.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is in use';
        errorDescription = 'Please close other apps using the microphone and try again.';
      }
      
      toast.error(errorMessage, { description: errorDescription });
      setIsAnswering(false);
      if (answerTimerRef.current) {
        clearInterval(answerTimerRef.current);
      }
    }
  };

  const stopAnswerRecording = async () => {
    if (answerTimerRef.current) {
      clearInterval(answerTimerRef.current);
    }
    
    // Minimum recording time check
    if (answerRecordingTime < 3) {
      toast.warning('Please speak for at least 3 seconds');
      return;
    }
    
    setIsAnswering(false);
    setIsTranscribing(true);
    
    // Stop recording
    if (answerMediaRecorderRef.current && answerMediaRecorderRef.current.state !== 'inactive') {
      answerMediaRecorderRef.current.stop();
      
      // Stop all audio tracks
      answerMediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    
    // Wait a moment for final data
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Create audio blob and transcribe
    try {
      if (answerChunksRef.current.length === 0) {
        throw new Error('No audio recorded');
      }
      
      const audioBlob = new Blob(answerChunksRef.current, { 
        type: answerMediaRecorderRef.current?.mimeType || 'audio/webm' 
      });
      
      // Send to transcription endpoint
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.webm');
      
      const transcribeRes = await api.post(`/evolve/sessions/${sessionId}/transcribe-answer`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (transcribeRes.data.success && transcribeRes.data.transcript) {
        setCurrentTranscript(transcribeRes.data.transcript);
        
        // Analyze speech for coaching feedback AND save to history
        analyzeSpeech(transcribeRes.data.transcript, answerRecordingTime, true, 'evolve', sessionId);
        
        // Evaluate the transcribed answer
        await evaluateTranscribedAnswer(transcribeRes.data.transcript);
      } else {
        // Provide more specific error feedback
        const errorMsg = transcribeRes.data.error || 'Could not transcribe your answer.';
        const durationHint = transcribeRes.data.duration_hint || '';
        
        if (errorMsg.includes('speak clearly')) {
          toast.error('Could not understand audio', {
            description: 'Please speak more clearly, closer to the microphone, and ensure there is minimal background noise.'
          });
        } else if (durationHint) {
          toast.error(errorMsg, { description: durationHint });
        } else {
          toast.error(errorMsg, {
            description: 'Try speaking louder and more clearly. Recording should be at least 5 seconds.'
          });
        }
        setCurrentTranscript('');
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Failed to process your answer. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const evaluateTranscribedAnswer = async (transcript) => {
    if (!transcript || transcript.trim().length < 10) {
      setAnswerEvaluation({
        status: 'needs_more',
        quality_score: 10,
        feedback: 'Your answer was too brief. Please provide more detail.',
        follow_up_prompt: `Can you elaborate on that? The question was: "${questions[currentQuestion]}" - Please provide a comprehensive verbal explanation.`,
        tips: ['Speak for at least 15-20 seconds', 'Include specific examples', 'Explain your reasoning clearly']
      });
      return;
    }

    setIsEvaluating(true);
    
    try {
      const formData = new FormData();
      formData.append('question', questions[currentQuestion]);
      formData.append('answer', transcript);
      
      const response = await api.post(`/evolve/sessions/${sessionId}/evaluate-answer`, formData);
      setAnswerEvaluation(response.data);
      
      // If answer is good enough or we've had too many follow-ups, proceed
      if (response.data.status !== 'needs_more' || followUpCount >= 2) {
        // Save and move to next question after a delay to show feedback
        setTimeout(() => {
          saveAnswerAndProceed(transcript, response.data);
        }, 2000);
      }
      
    } catch (error) {
      console.error('Answer evaluation error:', error);
      const wordCount = transcript.split(/\s+/).length;
      if (wordCount < 15) {
        setAnswerEvaluation({
          status: 'needs_more',
          quality_score: 40,
          feedback: 'Your answer could be more detailed. Please elaborate.',
          follow_up_prompt: 'Can you provide more specific examples or reasoning?',
          tips: ['Add supporting details', 'Explain the "why" behind your answer']
        });
      } else {
        // Good enough, proceed
        saveAnswerAndProceed(transcript, { quality_score: 70, status: 'acceptable' });
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  const tryAgainAnswer = () => {
    setFollowUpCount(prev => prev + 1);
    setCurrentTranscript('');
    setAnswerEvaluation(null);
    toast.info('Record your improved answer');
  };

  const saveAnswerAndProceed = async (transcript, evaluation) => {
    // Save the answer
    try {
      await api.post(`/evolve/sessions/${sessionId}/qa-answers`, {
        answers: [{
          question_index: currentQuestion,
          question: questions[currentQuestion],
          answer: transcript,
          evaluation_score: evaluation?.quality_score || 50,
          time_taken: answerRecordingTime,
          answer_type: 'voice'
        }]
      });
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
    
    // Reset and move to next question
    setAnsweredQuestions([...answeredQuestions, currentQuestion]);
    setFollowUpCount(0);
    setCurrentTranscript('');
    setAnswerEvaluation(null);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      finishSession();
    }
  };

  const skipAndProceed = async () => {
    // Save minimal answer and move on
    await saveAnswerAndProceed(currentTranscript || '[No response]', { quality_score: 30, status: 'skipped' });
  };

  const finishSession = async () => {
    setStage('analyzing');
    setAnalyzing(true);
    stopMediaStream();
    
    try {
      // Mark session as complete
      await api.post(`/evolve/sessions/${sessionId}/complete`);
      
      // Run AI analysis
      toast.info('Analyzing your presentation...');
      const analyzeResponse = await api.post(`/evolve/sessions/${sessionId}/analyze`);
      
      // Check if analysis was successful
      if (analyzeResponse.data?.feedback?.overall_score !== undefined) {
        setStage('complete');
        toast.success('Analysis complete!', {
          description: `Score: ${analyzeResponse.data.feedback.overall_score}%`
        });
      } else {
        setStage('complete');
        toast.success('Analysis complete!');
      }
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? errorDetail.recommendation || errorDetail.message 
        : (error.response?.data?.detail || 'Analysis failed');
      
      toast.error('Analysis could not be completed', {
        description: errorMessage,
        duration: 6000
      });
      setStage('complete');
    } finally {
      setAnalyzing(false);
    }
  };

  const viewResults = useCallback(() => {
    navigate(`/evolve/feedback/${sessionId}`);
  }, [navigate, sessionId]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#E07A5F] mx-auto mb-4" />
            <p className="text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Loading session...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {session?.topic_name}
            </h1>
            <Badge 
              variant="secondary" 
              className={`
                ${stage === 'presenting' ? 'bg-rose-500/20 text-rose-400' : ''}
                ${stage === 'qa' ? 'bg-amber-500/20 text-amber-400' : ''}
                ${stage === 'analyzing' ? 'bg-blue-500/20 text-blue-400' : ''}
                ${stage === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : ''}
              `}
            >
              {stage === 'setup' && 'Setup'}
              {stage === 'presenting' && 'Recording'}
              {stage === 'qa' && 'Q&A'}
              {stage === 'analyzing' && 'Analyzing'}
              {stage === 'complete' && 'Complete'}
            </Badge>
          </div>
          
          {(stage === 'presenting' || stage === 'qa') && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-white">
                <Clock className="h-4 w-4 text-slate-400" />
                <span className="font-mono text-lg">{formatTime(stage === 'qa' ? answerRecordingTime : recordingTime)}</span>
              </div>
              {isRecording && (
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 bg-rose-500 rounded-full animate-pulse" />
                  <span className="text-rose-400 text-sm">REC</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-16 min-h-screen flex">
        
        {/* Slide Area */}
        <div className="flex-1 p-6 flex flex-col">
          
          {/* Setup Stage */}
          {stage === 'setup' && (
            <div className="flex-1 flex items-center justify-center">
              <Card className="bg-slate-800 border-slate-700 max-w-lg w-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-[#E07A5F]/20 flex items-center justify-center mx-auto mb-6">
                    <Video className="h-10 w-10 text-[#E07A5F]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Ready to Present?
                  </h2>
                  <p className="text-slate-400 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    You will present {slides.length} slides while being recorded. Make sure your camera and microphone are working.
                  </p>
                  
                  {/* Preview - Now with auto-start */}
                  <SetupMediaPreview 
                    videoRef={videoRef}
                    streamRef={streamRef}
                    cameraEnabled={cameraEnabled}
                    micEnabled={micEnabled}
                    toggleCamera={toggleCamera}
                    toggleMic={toggleMic}
                  />
                  
                  <Button 
                    size="lg"
                    onClick={startPresenting}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white w-full"
                    data-testid="start-presenting-btn"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Start Presenting
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Presenting Stage */}
          {stage === 'presenting' && (
            <>
              {/* Slide Display */}
              <div className="flex-1 flex items-center justify-center relative">
                <div className="w-full max-w-5xl aspect-video bg-slate-800 rounded-xl overflow-hidden shadow-2xl relative">
                  {slides[currentSlide] && (
                    <LiveSlideImage 
                      slideUrl={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide].url}`}
                      slideNumber={currentSlide + 1}
                    />
                  )}
                  
                  {/* Slide Number */}
                  <div className="absolute bottom-4 left-4">
                    <Badge className="bg-black/60 text-white">
                      {currentSlide + 1} / {slides.length}
                    </Badge>
                  </div>
                </div>
                
                {/* Navigation Arrows */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                  disabled={currentSlide === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-800/80 hover:bg-slate-700 text-white h-12 w-12"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                  disabled={currentSlide === slides.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-800/80 hover:bg-slate-700 text-white h-12 w-12"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </div>

              {/* Bottom Controls */}
              <div className="mt-6 flex items-center justify-center gap-4">
                {/* Slide Thumbnails */}
                <div className="flex items-center gap-2 overflow-x-auto pb-2 max-w-2xl">
                  {slides.map((slide, index) => (
                    <SlideThumbnail
                      key={slide.slide_number}
                      slideUrl={`${process.env.REACT_APP_BACKEND_URL}${slide.url}`}
                      slideNumber={slide.slide_number}
                      isActive={currentSlide === index}
                      onClick={() => setCurrentSlide(index)}
                    />
                  ))}
                </div>
                
                <Button 
                  onClick={finishPresenting}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="finish-presenting-btn"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Finish Presenting
                </Button>
              </div>
            </>
          )}

          {/* Q&A Stage - VOICE ONLY */}
          {stage === 'qa' && (
            <div className="flex-1 flex items-center justify-center">
              <Card className="bg-slate-800 border-slate-700 max-w-2xl w-full">
                <CardContent className="p-8">
                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                      <span>Question {currentQuestion + 1} of {questions.length}</span>
                      <span>{answeredQuestions.length} answered</span>
                    </div>
                    <Progress value={(answeredQuestions.length / questions.length) * 100} className="h-2" />
                  </div>
                  
                  {/* Voice-Only Notice */}
                  <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center gap-2">
                    <Mic className="h-4 w-4 text-blue-400" />
                    <span className="text-sm text-blue-300">Voice responses only - speak clearly into your microphone</span>
                  </div>
                  
                  {/* Question */}
                  <div className="bg-slate-900/50 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[#F2CC8F]/20">
                        <MessageSquare className="h-6 w-6 text-[#F2CC8F]" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {answerEvaluation?.status === 'needs_more' ? 'Please elaborate...' : `Question ${currentQuestion + 1}`}
                        </p>
                        <p className="text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {answerEvaluation?.follow_up_prompt || questions[currentQuestion]}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Transcribed Answer Display */}
                  {currentTranscript && (
                    <div className="mb-6 p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                      <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Your Response:</p>
                      <p className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        &ldquo;{currentTranscript}&rdquo;
                      </p>
                      <p className="text-xs text-slate-500 mt-2">
                        {currentTranscript.split(/\s+/).length} words
                      </p>
                    </div>
                  )}
                  
                  {/* Evaluation Feedback */}
                  {answerEvaluation && (
                    <div className={`mb-6 p-4 rounded-lg border ${
                      answerEvaluation.status === 'needs_more' 
                        ? 'bg-amber-500/10 border-amber-500/20' 
                        : 'bg-emerald-500/10 border-emerald-500/20'
                    }`}>
                      <div className="flex items-start gap-3">
                        {answerEvaluation.status === 'needs_more' ? (
                          <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                        ) : (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
                        )}
                        <div>
                          <p className={`font-medium mb-2 ${
                            answerEvaluation.status === 'needs_more' ? 'text-amber-300' : 'text-emerald-300'
                          }`}>
                            {answerEvaluation.feedback}
                          </p>
                          {answerEvaluation.status === 'needs_more' && answerEvaluation.tips && (
                            <ul className="text-sm text-amber-400/80 space-y-1">
                              {answerEvaluation.tips.map((tip, i) => (
                                <li key={`tip-${i}`}>• {tip}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Speech Coaching - Real-time delivery feedback */}
                  {(speechAnalytics || isSpeechAnalyzing) && answerEvaluation && (
                    <div className="mb-6">
                      <SpeechCoaching 
                        analytics={speechAnalytics}
                        isLoading={isSpeechAnalyzing}
                        compact={true}
                        onClose={clearSpeechAnalytics}
                      />
                    </div>
                  )}
                  
                  {/* Recording Indicator */}
                  {isAnswering && (
                    <div className="mb-6 p-6 rounded-xl bg-rose-500/10 border border-rose-500/30 text-center">
                      <div className="flex items-center justify-center gap-4 mb-4">
                        <span className="h-6 w-6 bg-rose-500 rounded-full animate-pulse" />
                        <span className="text-2xl font-mono text-white">{formatTime(answerRecordingTime)}</span>
                      </div>
                      <p className="text-rose-300 text-lg">Recording your answer...</p>
                      <p className="text-slate-400 text-sm mt-2">Speak clearly and provide a comprehensive response</p>
                    </div>
                  )}
                  
                  {/* Transcribing Indicator */}
                  {isTranscribing && (
                    <div className="mb-6 p-6 rounded-xl bg-blue-500/10 border border-blue-500/30 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-400 mx-auto mb-3" />
                      <p className="text-blue-300 text-lg">Transcribing your answer...</p>
                      <p className="text-slate-400 text-sm">Converting speech to text</p>
                    </div>
                  )}
                  
                  {/* Evaluating Indicator */}
                  {isEvaluating && (
                    <div className="mb-6 p-6 rounded-xl bg-purple-500/10 border border-purple-500/30 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-purple-400 mx-auto mb-3" />
                      <p className="text-purple-300 text-lg">Evaluating your response...</p>
                      <p className="text-slate-400 text-sm">AI is analyzing your answer</p>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    {!isAnswering && !isTranscribing && !isEvaluating && (
                      <>
                        {!answerEvaluation ? (
                          <Button 
                            size="lg"
                            onClick={startAnswering}
                            className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white w-full"
                            data-testid="start-answer-btn"
                          >
                            <Mic className="h-5 w-5 mr-2" />
                            Start Recording Your Answer
                          </Button>
                        ) : answerEvaluation.status === 'needs_more' && followUpCount < 2 ? (
                          <div className="flex gap-3">
                            <Button 
                              size="lg"
                              onClick={tryAgainAnswer}
                              className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white flex-1"
                            >
                              <Mic className="h-5 w-5 mr-2" />
                              Record Improved Answer
                            </Button>
                            <Button 
                              size="lg"
                              onClick={skipAndProceed}
                              variant="outline"
                              className="border-slate-600 text-slate-300"
                            >
                              Keep & Continue
                            </Button>
                          </div>
                        ) : null}
                      </>
                    )}
                    
                    {isAnswering && (
                      <Button 
                        size="lg"
                        onClick={stopAnswerRecording}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                        disabled={answerRecordingTime < 3}
                        data-testid="stop-answer-btn"
                      >
                        <Square className="h-5 w-5 mr-2" />
                        {answerRecordingTime < 3 ? `Speak for ${3 - answerRecordingTime} more seconds...` : 'Stop & Submit Answer'}
                      </Button>
                    )}
                  </div>
                  
                  {/* Tips */}
                  {!isAnswering && !answerEvaluation && (
                    <div className="mt-6 p-4 rounded-lg bg-slate-900/50">
                      <p className="text-sm font-medium text-slate-300 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Tips for a Great Verbal Answer:
                      </p>
                      <ul className="text-xs text-slate-400 space-y-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <li>• Speak for at least 15-20 seconds</li>
                        <li>• Start with a clear, direct response</li>
                        <li>• Include specific examples or evidence</li>
                        <li>• Explain your reasoning process</li>
                        <li>• Conclude with a summary point</li>
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Analyzing Stage */}
          {stage === 'analyzing' && (
            <div className="flex-1 flex items-center justify-center">
              <Card className="bg-slate-800 border-slate-700 max-w-lg w-full">
                <CardContent className="p-8 text-center">
                  <Loader2 className="h-16 w-16 animate-spin text-[#E07A5F] mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Analyzing Your Performance
                  </h2>
                  <p className="text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Our AI is evaluating your presentation across 7 categories...
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Complete Stage */}
          {stage === 'complete' && (
            <div className="flex-1 flex items-center justify-center">
              <Card className="bg-slate-800 border-slate-700 max-w-lg w-full">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Session Complete!
                  </h2>
                  <p className="text-slate-400 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Great job! Your presentation has been analyzed. View your detailed feedback and scores.
                  </p>
                  <Button 
                    size="lg"
                    onClick={viewResults}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    data-testid="view-results-btn"
                  >
                    View Results
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Webcam Preview (floating during presentation) */}
        {(stage === 'presenting' || stage === 'qa') && (
          <div className="fixed bottom-24 right-6 w-64 aspect-video rounded-xl overflow-hidden shadow-2xl border-2 border-slate-700 bg-slate-900">
            <video 
              ref={videoRef}
              autoPlay 
              playsInline 
              muted 
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              <button
                onClick={toggleCamera}
                className={`p-1.5 rounded-full ${cameraEnabled ? 'bg-slate-700 text-white' : 'bg-rose-500/80 text-white'}`}
              >
                {cameraEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
              </button>
              <button
                onClick={toggleMic}
                className={`p-1.5 rounded-full ${micEnabled ? 'bg-slate-700 text-white' : 'bg-rose-500/80 text-white'}`}
              >
                {micEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Setup Media Preview Component - Auto-initializes camera/mic preview
const SetupMediaPreview = ({ videoRef, streamRef, cameraEnabled, micEnabled, toggleCamera, toggleMic }) => {
  const [previewActive, setPreviewActive] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    
    const initPreview = async () => {
      try {
        const constraints = {
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true
          }
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        if (!mounted) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        localStreamRef.current = stream;
        streamRef.current = stream;
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        
        setPreviewActive(true);
        setPermissionError(null);
        
      } catch (error) {
        console.error('Preview init error:', error);
        if (error.name === 'NotAllowedError') {
          setPermissionError('Camera/Microphone access denied. Please allow access in your browser settings.');
        } else if (error.name === 'NotFoundError') {
          setPermissionError('No camera or microphone found. Please connect a device.');
        } else if (error.name === 'NotReadableError') {
          setPermissionError('Device is in use by another application.');
        } else {
          setPermissionError('Could not access camera/microphone. Please check your settings.');
        }
      }
    };
    
    initPreview();
    
    return () => {
      mounted = false;
      // Don't stop the stream here - the parent component will manage it
      // Only stop if it wasn't passed to the parent
      if (localStreamRef.current && localStreamRef.current !== streamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [streamRef, videoRef]);

  const handleToggleCamera = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        toggleCamera();
      }
    }
  };

  const handleToggleMic = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        toggleMic();
      }
    }
  };

  return (
    <div className="mb-6">
      {/* Preview Area */}
      <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 relative mb-4">
        <video 
          ref={localVideoRef}
          autoPlay 
          playsInline 
          muted 
          className="w-full h-full object-cover"
        />
        {!previewActive && !permissionError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#E07A5F] mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Initializing camera...</p>
            </div>
          </div>
        )}
        {permissionError && (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-amber-400 text-sm">{permissionError}</p>
            </div>
          </div>
        )}
        {previewActive && (
          <div className="absolute top-2 right-2">
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/80 text-white text-xs">
              <span className="h-2 w-2 bg-white rounded-full animate-pulse" />
              Preview Active
            </span>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="lg"
          onClick={handleToggleCamera}
          disabled={!previewActive}
          className={`border-slate-600 ${cameraEnabled ? 'text-white' : 'text-slate-500 bg-rose-500/20'}`}
        >
          {cameraEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={handleToggleMic}
          disabled={!previewActive}
          className={`border-slate-600 ${micEnabled ? 'text-white' : 'text-slate-500 bg-rose-500/20'}`}
        >
          {micEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </Button>
      </div>
    </div>
  );
};


// LiveSlideImage - Loads slide images with credentials for httpOnly cookie auth
const LiveSlideImage = ({ slideUrl, slideNumber }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    
    const loadImage = async () => {
      try {
        const response = await fetch(slideUrl, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to load slide');
        }
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        if (mounted) {
          setImageSrc(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Slide load error:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    
    loadImage();
    
    return () => {
      mounted = false;
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [slideUrl]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#E07A5F] mx-auto mb-3" />
          <p className="text-slate-400">Loading Slide {slideNumber}...</p>
        </div>
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <p className="text-white text-lg mb-1">Slide {slideNumber}</p>
          <p className="text-slate-400 text-sm">Preview unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={`Slide ${slideNumber}`}
      className="w-full h-full object-contain bg-white"
    />
  );
};


// SlideThumbnail - Thumbnail with credential-based image loading
const SlideThumbnail = ({ slideUrl, slideNumber, isActive, onClick }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      try {
        const response = await fetch(slideUrl, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed');
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        if (mounted) {
          setImageSrc(objectUrl);
          setLoaded(true);
        }
      } catch (err) {
        console.error('Thumbnail load error:', err);
      }
    };
    
    loadImage();
    
    return () => {
      mounted = false;
      if (imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [slideUrl]);

  return (
    <button
      onClick={onClick}
      className={`
        w-20 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0
        ${isActive 
          ? 'border-[#E07A5F] ring-2 ring-[#E07A5F]/50' 
          : 'border-slate-700 opacity-60 hover:opacity-100'
        }
      `}
    >
      {loaded && imageSrc ? (
        <img
          src={imageSrc}
          alt={`Slide ${slideNumber}`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-slate-700 flex items-center justify-center">
          <span className="text-xs text-slate-400">{slideNumber}</span>
        </div>
      )}
    </button>
  );
};

export default EvolveLiveSession;
